import { type ReactNode, useState } from 'react';
import type { CameraSource } from '@/camera/cameraSource';
import { useBridgeCamera } from '@/camera/useBridgeCamera';
import { useLocalCamera } from '@/camera/useLocalCamera';

type OnFiles = (files: File[]) => void;

function useShutter(source: CameraSource, onFiles: OnFiles) {
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState('');
  const shoot = async () => {
    if (!source.ready) return;
    setError('');
    setFlash(true);
    setTimeout(() => setFlash(false), 150);
    try {
      const blob = await source.capture();
      onFiles([new File([blob], `photo-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' })]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not take photo');
    }
  };
  return { shoot, flash, error };
}

function CameraTile({
  caption,
  source,
  shutter,
  children,
}: {
  caption: string;
  source: CameraSource;
  shutter: ReturnType<typeof useShutter>;
  /** The preview, filling the tile with `object-contain`. */
  children: ReactNode;
}) {
  return (
    <figure aria-label={caption} className="relative m-0 min-h-0 overflow-hidden bg-black">
      {children}
      {shutter.flash && <div aria-hidden="true" className="absolute inset-0 bg-white/70" />}
      {source.error && (
        <output className="absolute inset-0 flex items-center justify-center bg-black/60 px-4 text-center">
          {source.error}
        </output>
      )}
      <figcaption className="absolute inset-x-0 top-0 truncate bg-black/40 px-2 py-1 text-xs">
        {caption}
      </figcaption>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 pb-2">
        {shutter.error && (
          <p role="alert" className="rounded bg-black/60 px-2 text-center text-sm">
            {shutter.error}
          </p>
        )}
        <button
          type="button"
          aria-label="Shutter"
          disabled={!source.ready}
          onClick={() => void shutter.shoot()}
          className="h-14 w-14 rounded-full border-4 border-white bg-white/90 ring-2 ring-black ring-inset transition active:scale-95 disabled:opacity-40"
        />
      </div>
    </figure>
  );
}

const PREVIEW = 'absolute inset-0 h-full w-full object-contain';

export function LocalCameraTile({
  deviceId,
  zoom,
  onFiles,
}: {
  deviceId?: string;
  zoom?: number;
  onFiles: OnFiles;
}) {
  const camera = useLocalCamera(true, deviceId, zoom);
  const shutter = useShutter(camera, onFiles);
  const lens = camera.devices.find((d) => d.deviceId === camera.deviceId)?.label;
  return (
    <CameraTile
      caption={lens ? `This device · ${lens}` : 'This device'}
      source={camera}
      shutter={shutter}
    >
      <video ref={camera.videoRef} autoPlay playsInline muted className={PREVIEW} />
    </CameraTile>
  );
}

export function BridgeCameraTile({
  bridgeId,
  name,
  onFiles,
}: {
  bridgeId: string;
  /** The saved name, shown until the bridge answers. */
  name: string;
  onFiles: OnFiles;
}) {
  const camera = useBridgeCamera(bridgeId, () => void shutter.shoot());
  const shutter = useShutter(camera, onFiles);
  return (
    <CameraTile caption={camera.caption || name} source={camera} shutter={shutter}>
      {camera.frameUrl && <img src={camera.frameUrl} alt="Camera preview" className={PREVIEW} />}
    </CameraTile>
  );
}
